import { LiveAnnouncer } from "@angular/cdk/a11y";
import { CurrencyPipe } from "@angular/common";
import { Component, inject, type OnInit, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, type NgForm } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatPaginator, MatPaginatorModule } from "@angular/material/paginator";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSelectModule } from "@angular/material/select";
import { MatSort, MatSortModule, type Sort } from "@angular/material/sort";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { CurrencyService } from "../currency/currency.service";
import { CurrencyValidatorDirective } from "../currency/validators/currency-validator.directive";
import { MaxOverMinValidatorDirective } from "../currency/validators/max-over-min-validator.directive";
import { DialogPresenter } from "../medi-dialog/DialogPresenter";
import { OroService } from "../oroservice";
import { CaseService } from "./case.service";
import { CaseDetail, type DialogData } from "./case-detail";
import type { Case } from "./models/case";
import type { CaseListRow } from "./models/case-list-row";
import type { Search } from "./models/search";
import type { SearchRequest } from "./models/search-request";

@Component({
  selector: "CaseList",
  templateUrl: "./case-list.html",
  styleUrls: ["./case-list.scss"],
  standalone: true,
  imports: [
    CurrencyPipe,
    FormsModule,
    CurrencyValidatorDirective,
    MaxOverMinValidatorDirective,
    TranslateModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSortModule,
    MatTableModule,
  ],
})
export class CaseList implements OnInit {
  public caseColumnsFull: string[] = [
    "selection",
    "Id",
    "Status",
    "Rechnungsnummern",
    "Betrag",
    "Rechnungsempfaenger",
    "RechnungsempfaengerAnschrift",
    "Behandler",
  ];
  public caseColumns: string[] = [
    "selection",
    "Id",
    "Status",
    "Rechnungsnummern",
    "Betrag",
    "Rechnungsempfaenger",
    "RechnungsempfaengerAnschrift",
  ];
  public displayedColumns: string[] = this.caseColumns;
  public cases: CaseListRow[] = [];
  public dataSource = new MatTableDataSource(this.cases);
  public oldPageSize: number = 10;
  public update: boolean = false;
  public isLossCertificateWithdrawalInProgress: boolean = false;
  public search: Search = {
    Behandler: -1,
    Status: 0,
    Art: 0,
    MinimumAmount: "",
    MaximumAmount: "",
  };
  public searchStatus: number[];
  public allSelected = false;

  @ViewChild(MatSort) public sort: MatSort;
  @ViewChild(MatPaginator) public paginator: MatPaginator;
  @ViewChild("searchForm") public searchForm: NgForm;

  private readonly oroService = inject(OroService);
  private readonly caseService = inject(CaseService);
  private readonly currencyService = inject(CurrencyService);
  private readonly dialog = inject(DialogPresenter);
  private readonly liveAnnouncer = inject(LiveAnnouncer);
  private readonly translate: TranslateService = inject(TranslateService);
  private defaultSearch = { ...this.search };

  public constructor() {
    this.subscribeToLanguageChanges();
  }

  public async ngOnInit(): Promise<void> {
    this.update = true;

    if (this.oroService.kundeninfo?.Behandler?.length > 0) {
      this.displayedColumns = this.caseColumnsFull;
    }

    await this.oroService.getKundenInfo();
    await this.refresh();
  }

  public ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.paginator._intl.itemsPerPageLabel = "Einträge pro Seite";
    this.dataSource.paginator = this.paginator;
    this.oldPageSize = this.paginator.pageSize;
  }

  /** Announce the change in sort state for assistive technology. */
  public async announceSortChange(sortState: Sort): Promise<void> {
    // This example uses English messages. If your application supports
    // multiple language, you would internationalize these strings.
    // Furthermore, you can customize the message to add additional
    // details about the values being sorted.
    if (sortState.direction) {
      await this.liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      await this.liveAnnouncer.announce("Sorting cleared");
    }
    this.dataSource.sort = this.sort;
  }

  public async downloadExcel(): Promise<void> {
    const selectedCases = this.cases.filter((item) => item.Selected);

    if (selectedCases.length === 0) {
      return;
    }

    await this.oroService.getDossiersExcel(selectedCases);
  }

  public async clearSearch(): Promise<void> {
    this.search = { ...this.defaultSearch };
    this.searchStatus = [];
    this.searchForm.resetForm({ ...this.search, searchStatus: this.searchStatus });
    await this.applySearch();
  }

  public hasMinimumAmountFilter(): boolean {
    return this.search.MinimumAmount.trim() !== "";
  }

  public hasMaximumAmountFilter(): boolean {
    return this.search.MaximumAmount.trim() !== "";
  }

  public async clearMinimumAmountFilter(): Promise<void> {
    this.search.MinimumAmount = "";
    this.searchForm.controls.MinimumAmount?.reset("");
    await this.applySearch();
  }

  public async clearMaximumAmountFilter(): Promise<void> {
    this.search.MaximumAmount = "";
    this.searchForm.controls.MaximumAmount?.reset("");
    await this.applySearch();
  }

  public clearPortfolio(): void {
    this.search.Art = 0;
  }

  public clearSearchStatus(): void {
    this.searchStatus = [];
  }

  public getStatusKey(status: number): string {
    switch (status) {
      case 1:
        return 'DossierStatus.NichtGestartet';
      case 2:
        return 'DossierStatus.VorrechtlichInkasso';
      case 3:
        return 'DossierStatus.RechtlichesInkasso';
      case 4:
        return 'DossierStatus.Ratenplan';
      case 5:
        return 'DossierStatus.Abgerechnet';
      default:
        return '?';
    }
  }

  public canDownload(): boolean {
    return this.cases.some((item) => item.Selected);
  }

  public canClearSearch(): boolean {
    let isModified = false;
    isModified ||= !!this.search.Dossiernummer;
    isModified ||= !!this.search.RechnungsNr;
    isModified ||= this.search.Art !== 0;
    isModified ||= this.searchStatus?.length > 0;
    isModified ||= this.search.MinimumAmount.trim() !== "";
    isModified ||= this.search.MaximumAmount.trim() !== "";
    isModified ||= !!this.search.VornameRE;
    isModified ||= !!this.search.NameRE;
    isModified ||= !!this.search.AnschriftRE;
    return isModified;
  }

  public canWithdrawLossCertificates(): boolean {
    return this.cases.some((item) => item.Selected && item.CanWithdrawLossCertificate);
  }

  public async withdrawLossCertificates(): Promise<void> {
    const validCases = this.cases.filter((item) => item.Selected && item.CanWithdrawLossCertificate);

    if (validCases.length === 0) {
      return;
    }

    this.isLossCertificateWithdrawalInProgress = true;
    try {
      if (await this.caseService.withdrawLossCertificates(validCases)) {
        await this.applySearch();
      }
    } finally {
      this.isLossCertificateWithdrawalInProgress = false;
    }
  }

  public async openCaseDetails(caseId: number): Promise<void> {
    const loadedCase = await this.caseService.getCase(caseId);
    if (loadedCase != null) {
      this.openCaseDialog(loadedCase);
    }
  }

  public toggleSelection(): void {
    for (const caseItem of this.cases) {
      caseItem.Selected = this.allSelected;
    }
  }

  public get practitioners(): NonNullable<OroService["kundeninfo"]["Behandler"]> {
    return this.oroService.kundeninfo?.Behandler ?? [];
  }

  public getPractitionerName(id?: number): string {
    if (id == null) {
      return "";
    }

    return this.oroService.getBehandler(id);
  }

  public hasPractitioners(): boolean {
    return this.practitioners.length > 0;
  }

  public updateValidator(): void {
    if (this.oldPageSize !== this.paginator.pageSize) {
      this.paginator.firstPage();
    }

    this.oldPageSize = this.paginator.pageSize;
  }

  public isInvoicedOptionDisabled(): boolean {
    return this.searchStatus?.indexOf(1) > -1;
  }

  public isNotInvoicedOptionDisabled(): boolean {
    return this.searchStatus?.indexOf(2) > -1;
  }

  public isPreMigrationOptionDisabled(): boolean {
    return this.searchStatus?.indexOf(3) > -1;
  }

  public isPostMigrationOptionDisabled(): boolean {
    return this.searchStatus?.indexOf(4) > -1;
  }

  public async applySearch(): Promise<void> {
    this.search.Unlimited = true;

    if (this.searchStatus?.indexOf(1) > -1) {
      this.search.StatusAbrechnung = 1;
    } else if (this.searchStatus?.indexOf(2) > -1) {
      this.search.StatusAbrechnung = 2;
    } else {
      this.search.StatusAbrechnung = 0;
    }

    if (this.searchStatus?.indexOf(3) > -1) {
      this.search.StatusMigration = 1;
    } else if (this.searchStatus?.indexOf(4) > -1) {
      this.search.StatusMigration = 2;
    } else {
      this.search.StatusMigration = 0;
    }

    const minimumValueResult = this.currencyService.parseValue(this.search.MinimumAmount);
    const maximumValueResult = this.currencyService.parseValue(this.search.MaximumAmount);
    if (this.searchForm?.invalid) {
      return;
    }

    const searchRequest = this.createSearchRequest(minimumValueResult.value, maximumValueResult.value);
    this.cases = await this.caseService.searchCases(searchRequest);
    this.dataSource = new MatTableDataSource(this.cases);
    this.initializeSorting();
    this.dataSource.paginator = this.paginator;
  }

  private openCaseDialog(selectedCase: Case): void {
    this.dialog.openComponent<CaseDetail, DialogData, boolean>(CaseDetail, {
      width: "850px",
      disableClose: true,
      data: { dossierinfo: selectedCase, refreshDossiers: () => this.applySearch() },
      autoFocus: false,
    });
  }

  private async refresh(): Promise<void> {
    await this.loadAllCases();
  }

  private async loadAllCases(): Promise<void> {
    this.update = true;
    this.search.Unlimited = true;
    try {
      const minimumValueResult = this.currencyService.parseValue(this.search.MinimumAmount);
      const maximumValueResult = this.currencyService.parseValue(this.search.MaximumAmount);
      if (this.searchForm?.invalid) {
        return;
      }

      const searchRequest = this.createSearchRequest(minimumValueResult.value, maximumValueResult.value);
      this.cases = await this.caseService.searchCases(searchRequest);
      this.dataSource = new MatTableDataSource(this.cases);
      this.initializeSorting();
      this.dataSource.paginator = this.paginator;
    } finally {
      this.update = false;
    }
  }

  private initializeSorting(): void {
    const defaultSort = this.dataSource.sortData.bind(this.dataSource);
    this.dataSource.sortData = (data: CaseListRow[], sort: MatSort): CaseListRow[] => {
      if (sort.active !== 'Status' || !sort.direction) {
        return defaultSort(data, sort);
      }

      const language = this.translate.currentLang || this.translate.defaultLang || 'de';
      const collator = new Intl.Collator(language);
      const direction = sort.direction === 'asc' ? 1 : -1;
      return data.sort(
        (left, right) => direction * collator.compare(
          this.translate.instant(this.getStatusKey(left.Status)),
          this.translate.instant(this.getStatusKey(right.Status))
        )
      );
    };
    this.dataSource.sort = this.sort ?? null;
  }

  private subscribeToLanguageChanges(): void {
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe(() => {
      if (this.sort?.active === 'Status') {
        this.dataSource.data = [...this.cases];
      }
    });
  }

  private createSearchRequest(minimumAmount: number | null, maximumAmount: number | null): SearchRequest {
    return {
      ...this.search,
      MinimumAmount: minimumAmount,
      MaximumAmount: maximumAmount,
    };
  }
}
